import cx from "classnames";
import { ReactNode, useCallback, useId } from "react";

import { BaseSelectItem } from "ds/components/Select/types";
import TextEllipsis from "ds/components/TextEllipsis";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";

import styles from "./styles.module.css";

type FilterOptionProps = {
  value: string;
  label: string;
  postfix?: ReactNode;
  onChange?: (value: string, option?: BaseSelectItem) => void;
  checked?: boolean;
  indeterminate?: boolean;
  className?: string;
  disabled?: boolean;
};

const FilterOption = (props: FilterOptionProps) => {
  const { disabled, value, label, onChange, checked, indeterminate, postfix, className } = props;

  // If the label comes as an empty string (""), it is replaced with a visual placeholder.
  const displayLabel = label || "Empty";

  const id = useId();

  const handleOnChange = useCallback(() => {
    onChange?.(value, {
      value,
      label,
    });

    return undefined;
  }, [label, onChange, value]);

  return (
    <Toggle
      className={cx(styles.item, className)}
      variant="checkbox"
      id={id}
      onChange={handleOnChange}
      isSelected={checked}
      isIndeterminate={indeterminate}
      isDisabled={disabled}
      align="center"
    >
      <TextEllipsis
        tooltip={displayLabel}
        tooltipPlacement="top-end"
        tooltipWidthMode="maxWidthXl"
        delay={400}
        mode="single"
      >
        {(ellipsisProps) => (
          <Typography {...ellipsisProps} tag="span">
            {displayLabel}
          </Typography>
        )}
      </TextEllipsis>

      {postfix !== undefined && (
        <Typography tag="span" className={styles.postfix}>
          {postfix}
        </Typography>
      )}
    </Toggle>
  );
};

export default FilterOption;
